import { Component, Input, Output, EventEmitter, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TreeViewModule, SelectableSettings } from '@progress/kendo-angular-treeview';
import { IconsModule } from '@progress/kendo-angular-icons';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import { DirectoryNode as ApiDirectoryNode } from '../../models/file-vault.models';
import { Observable, map } from 'rxjs';

export interface ExplorerNode {
  id: string;
  name: string;
  fullPath: string;
  isExpanded: boolean;
  isSelected: boolean;
  hasChildren: boolean;
  fileCount?: number;
  children: ExplorerNode[];
}

@Component({
  selector: 'fv-file-explorer',
  standalone: true,
  template: `
    <div class="file-explorer">
      <div class="explorer-header">
        <span class="explorer-title">{{ title }}</span>
        <button kendoButton (click)="refresh()" title="Refresh">Refresh</button>
      </div>
      <kendo-treeview
        [nodes]="rootNodes"
        textField="name"
        kendoTreeViewExpandable
        expandBy="fullPath"
        [expandedKeys]="expandedKeys"
        (expandedKeysChange)="onExpandedKeysChange($event)"
        kendoTreeViewSelectable
        [(selectedKeys)]="selectedKeys"
        (selectionChange)="onSelectionChange($event)"
        [hasChildren]="hasChildren"
        [children]="fetchChildren"
        class="tree-view">
        <ng-template kendoTreeViewNodeTemplate let-dataItem>
          <span class="node-template">
            <span class="folder-icon">📁</span>
            <span class="node-text">{{ dataItem.name }}</span>
          </span>
        </ng-template>
      </kendo-treeview>
      <div *ngIf="isLoading" class="loading-overlay">
        <kendo-loader type="pulsing"></kendo-loader>
      </div>
    </div>
  `,
  styles: [`
    .file-explorer {
      height: 100%;
      display: flex;
      flex-direction: column;
      border: 1px solid var(--fv-border);
      border-radius: 4px;
      background: var(--fv-surface);
      position: relative;
    }
    .explorer-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 12px;
      border-bottom: 1px solid var(--fv-border);
      background: var(--fv-surface-hover);
    }
    .explorer-title { font-weight: 600; font-size: 14px; }
    .tree-view { flex: 1; overflow: auto; padding: 8px; }
    .node-template { display: flex; align-items: center; gap: 6px; }
    .folder-icon { font-size: 16px; }
    .node-text { cursor: pointer; }
    .loading-overlay {
      position: absolute; top: 0; left: 0; right: 0; bottom: 0;
      background: var(--fv-surface-overlay);
      display: flex; align-items: center; justify-content: center;
    }
  `],
  imports: [CommonModule, TreeViewModule, IconsModule, ButtonsModule, IndicatorsModule]
})
export class FileExplorerComponent implements OnInit {
  private api = inject(FileVaultApiService);

  @Input() title = 'File Explorer';
  @Input() rootPath = '';
  @Input() showHidden = false;

  @Output() directorySelected = new EventEmitter<ExplorerNode>();
  @Output() nodeExpanded = new EventEmitter<ExplorerNode>();

  rootNodes: ExplorerNode[] = [];
  expandedKeys: string[] = [];
  selectedKeys: string[] = [];
  isLoading = false;

  selectableSettings: SelectableSettings = { mode: 'single' };

  ngOnInit(): void {
    this.loadRootDirectory();
  }

  async loadRootDirectory(): Promise<void> {
    this.isLoading = true;
    try {
      const directories = await this.api.getDirectoryTree(this.rootPath, 1).toPromise();
      this.rootNodes = this.mapDirectoriesToNodes(directories || []);
    } catch (error) {
      console.error('Failed to load root directory', error);
    } finally {
      this.isLoading = false;
    }
  }

  mapDirectoriesToNodes(directories: ApiDirectoryNode[]): ExplorerNode[] {
    return directories.map(dir => ({
      id: dir.fullPath,
      name: dir.name,
      fullPath: dir.fullPath,
      isExpanded: dir.isExpanded,
      isSelected: dir.isSelected,
      hasChildren: dir.childrenCount > 0,
      fileCount: dir.fileCount,
      children: []
    }));
  }

  /**
   * Callback function for Kendo TreeView to determine if a node has children
   */
  public hasChildren = (node: any): boolean => {
    return node.hasChildren === true;
  };

  /**
   * Callback function for Kendo TreeView to fetch children - returns an Observable
   * This is the key to lazy loading - Kendo calls this when a node is expanded
   */
  public fetchChildren = (node: any): Observable<ExplorerNode[]> => {
    console.log('fetchChildren called for:', node.fullPath);
    return this.api.getDirectoryTree(node.fullPath, 1).pipe(
      map(directories => {
        console.log('Loaded children for', node.fullPath, directories);
        const children = this.mapDirectoriesToNodes(directories || []);
        this.nodeExpanded.emit(node);
        return children;
      })
    );
  };

  onExpandedKeysChange(keys: string[]): void {
    console.log('Expanded keys changed:', keys);
    this.expandedKeys = keys;
  }

  onSelectionChange(event: { index: string; dataItem: ExplorerNode }): void {
    this.directorySelected.emit(event.dataItem);
  }

  refresh(): void {
    this.expandedKeys = [];
    this.selectedKeys = [];
    this.loadRootDirectory();
  }
}
